import React, { useState, useRef } from 'react';
import { 
  Zap, 
  Clipboard, 
  RefreshCw, 
  RotateCcw, 
  Bug, 
  Play, 
  Square, 
  Download,
  Settings,
  Eye,
  EyeOff
} from 'lucide-react';

const TestControlPanel = ({ 
  isOpen, 
  onClose, 
  onAutoFill, 
  onCreateTest, 
  onValidate, 
  onReset, 
  onDebug, 
  onRunSuite,
  isRunning = false,
  testResults = null,
  formRef = null
}) => {
  const [selectedScenario, setSelectedScenario] = useState('default');
  const [debugMode, setDebugMode] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const scenarios = [
    { value: 'default', label: 'Default Data' },
    { value: 'valid', label: 'Valid Data' },
    { value: 'invalid', label: 'Invalid Data' },
    { value: 'edgeCase', label: 'Edge Cases' }
  ];

  const handleAutoFill = () => {
    onAutoFill(selectedScenario, formRef);
  };

  const handleCreateTest = () => {
    onCreateTest(selectedScenario);
  };

  const handleValidate = () => {
    onValidate();
  };

  const handleReset = () => {
    onReset(formRef);
  };

  const handleDebug = () => {
    setDebugMode(!debugMode);
    onDebug(!debugMode);
  };

  const handleRunSuite = () => {
    onRunSuite();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: '600px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Bug size={24} style={{ color: '#007B8F' }} />
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1F2937',
              margin: 0
            }}>
              Test Automation Panel
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#F3F4F6',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}
            >
              {showAdvanced ? <EyeOff size={16} /> : <Eye size={16} />}
              {showAdvanced ? 'Hide' : 'Show'} Advanced
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Square size={20} style={{ color: '#6B7280' }} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '24px'
        }}>
          {/* Scenario Selection */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Test Scenario
            </label>
            <select
              value={selectedScenario}
              onChange={(e) => setSelectedScenario(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
              }}
            >
              {scenarios.map(scenario => (
                <option key={scenario.value} value={scenario.value}>
                  {scenario.label}
                </option>
              ))}
            </select>
          </div>

          {/* Test Controls */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            {/* Auto-Fill Button */}
            <button
              onClick={handleAutoFill}
              disabled={isRunning}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '16px',
                backgroundColor: '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isRunning ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isRunning ? 0.6 : 1
              }}
            >
              <Zap size={16} />
              Auto-Fill
            </button>

            {/* Create Test Button */}
            <button
              onClick={handleCreateTest}
              disabled={isRunning}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '16px',
                backgroundColor: '#F59E0B',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isRunning ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isRunning ? 0.6 : 1
              }}
            >
              <Clipboard size={16} />
              Create Test
            </button>

            {/* Validate Button */}
            <button
              onClick={handleValidate}
              disabled={isRunning}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '16px',
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isRunning ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isRunning ? 0.6 : 1
              }}
            >
              <RefreshCw size={16} />
              Validate
            </button>

            {/* Reset Button */}
            <button
              onClick={handleReset}
              disabled={isRunning}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '16px',
                backgroundColor: '#6B7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isRunning ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isRunning ? 0.6 : 1
              }}
            >
              <RotateCcw size={16} />
              Reset
            </button>
          </div>

          {/* Advanced Controls */}
          {showAdvanced && (
            <div style={{
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              backgroundColor: '#F9FAFB'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1F2937',
                margin: '0 0 16px 0'
              }}>
                Advanced Controls
              </h3>
              
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '16px'
              }}>
                {/* Debug Toggle */}
                <button
                  onClick={handleDebug}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    backgroundColor: debugMode ? '#8B5CF6' : '#F3F4F6',
                    color: debugMode ? 'white' : '#374151',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <Bug size={16} />
                  {debugMode ? 'Debug ON' : 'Debug OFF'}
                </button>

                {/* Run Test Suite */}
                <button
                  onClick={handleRunSuite}
                  disabled={isRunning}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    backgroundColor: '#DC2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isRunning ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: isRunning ? 0.6 : 1
                  }}
                >
                  <Play size={16} style={{ 
                    animation: isRunning ? 'spin 1s linear infinite' : 'none' 
                  }} />
                  {isRunning ? 'Running...' : 'Run Test Suite'}
                </button>
              </div>

              {/* Test Results Summary */}
              {testResults && (
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  padding: '12px'
                }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: '0 0 8px 0'
                  }}>
                    Last Test Results
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '8px',
                    fontSize: '12px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: '600', color: '#1F2937' }}>
                        {testResults.totalTests || 0}
                      </div>
                      <div style={{ color: '#6B7280' }}>Total</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: '600', color: '#10B981' }}>
                        {testResults.passed || 0}
                      </div>
                      <div style={{ color: '#6B7280' }}>Passed</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: '600', color: '#EF4444' }}>
                        {testResults.failed || 0}
                      </div>
                      <div style={{ color: '#6B7280' }}>Failed</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: '600', color: '#F59E0B' }}>
                        {testResults.errors || 0}
                      </div>
                      <div style={{ color: '#6B7280' }}>Errors</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Status */}
          {isRunning && (
            <div style={{
              padding: '16px',
              backgroundColor: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                color: '#92400E',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Running tests...
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #E5E7EB',
          backgroundColor: '#F9FAFB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '12px', color: '#6B7280' }}>
            Development Mode Only
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007B8F',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestControlPanel;
